/* Goldie — Guide Article: table of contents.
   Custom element <goldie-toc>. Builds its list from the <h2> elements in the
   rendered article body — a sibling in the DOM, since Shopify's article body
   can't live inside this element — assigns stable slug ids to headings that
   lack one, and collapses overflow behind a toggle. Loaded with defer. */

class GoldieToc extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('#tocList');
    this.toggleBtn = this.querySelector('#tocToggle');
    this.body = document.querySelector('[data-article-body]');
    if (!this.list || !this.body) return;

    this.visible = parseInt(this.dataset.visibleItems, 10) || 6;
    this.heads = this.#collectHeadings();

    if (!this.heads.length) {
      this.hidden = true;
      return;
    }

    this.#assignIds();
    this.#renderList();
    this.#setupScrollspy();
  }

  #collectHeadings() {
    const scope = this.closest('.goldie-guide') || document;
    return Array.from(scope.querySelectorAll('article h2, article h3')).filter((h) =>
      !h.closest('.pblock, .pinline, .bundle, .toc, aside, .newsletter, footer') &&
      h.textContent.trim().length > 1 &&
      h.dataset.tocSkip === undefined
    );
  }

  #slugify(text) {
    return text
      .toLowerCase()
      .trim()
      .replace(/['’]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60);
  }

  #assignIds() {
    const used = new Set();
    this.heads.forEach((h, i) => {
      if (!h.id) {
        const base = this.#slugify(h.textContent) || `section-${i + 1}`;
        let id = base;
        let n = 2;
        while (used.has(id) || document.getElementById(id)) id = `${base}-${n++}`;
        h.id = id;
      }
      used.add(h.id);
    });
  }

  #renderList() {
    const frag = document.createDocumentFragment();
    this.heads.forEach((h, i) => {
      const li = document.createElement('li');
      if (i >= this.visible) li.className = 'hidden-item';
      const a = document.createElement('a');
      a.href = `#${h.id}`;
      a.textContent = h.dataset.tocLabel || h.textContent.trim();
      li.appendChild(a);
      frag.appendChild(li);
    });
    this.list.innerHTML = '';
    this.list.appendChild(frag);

    const hiddenCount = Math.max(0, this.heads.length - this.visible);
    if (!hiddenCount) {
      if (this.toggleBtn) this.toggleBtn.hidden = true;
      return;
    }
    if (!this.toggleBtn) return;

    this.toggleBtn.textContent = `View ${hiddenCount} More +`;
    this.toggleBtn.addEventListener('click', () => {
      const open = this.classList.toggle('open');
      this.toggleBtn.setAttribute('aria-expanded', String(open));
      this.toggleBtn.textContent = open ? 'Show Less –' : `View ${hiddenCount} More +`;
    });
  }

  #setupScrollspy() {
    if (!('IntersectionObserver' in window)) return;
    const links = new Map();
    this.list.querySelectorAll('a').forEach((a) => links.set(a.getAttribute('href').slice(1), a));
    const spy = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const a = links.get(entry.target.id);
        if (a) a.classList.toggle('is-current', entry.isIntersecting);
      });
    }, { rootMargin: '-90px 0px -70% 0px' });
    this.heads.forEach((h) => spy.observe(h));
  }
}

customElements.define('goldie-toc', GoldieToc);

/* Highlights unfilled [bracket] placeholder text (see docs/shortcodes.md)
   so editors notice it before publishing. Runs once per article, regardless
   of whether the TOC itself is enabled — this script is always loaded. */
const PLACEHOLDER_PATTERN = /\[[^[\]]+\]/g;

const highlightPlaceholders = (root) => {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) => {
      if (!node.nodeValue.includes('[')) return NodeFilter.FILTER_SKIP;
      if (node.parentElement?.closest('script, style, .placeholder')) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });

  const textNodes = [];
  let node;
  while ((node = walker.nextNode())) textNodes.push(node);

  textNodes.forEach((textNode) => {
    const text = textNode.nodeValue;
    PLACEHOLDER_PATTERN.lastIndex = 0;
    if (!PLACEHOLDER_PATTERN.test(text)) return;

    const frag = document.createDocumentFragment();
    let cursor = 0;
    let match;
    PLACEHOLDER_PATTERN.lastIndex = 0;
    while ((match = PLACEHOLDER_PATTERN.exec(text))) {
      if (match.index > cursor) frag.append(text.slice(cursor, match.index));
      const span = document.createElement('span');
      span.className = 'placeholder';
      span.textContent = match[0];
      frag.append(span);
      cursor = match.index + match[0].length;
    }
    if (cursor < text.length) frag.append(text.slice(cursor));
    textNode.replaceWith(frag);
  });
};

document.querySelectorAll('.goldie-guide article').forEach(highlightPlaceholders);
